/**
 * FR-CRM-1 — Bangladesh phone normalization.
 *
 * One customer per phone number only works if every entry point spells the
 * number the same way. Everything is normalized to E.164 (`+8801XXXXXXXXX`)
 * before storage or lookup; raw user input never reaches a query.
 *
 * A BD mobile number is `01` + operator digit (3–9) + 8 subscriber digits.
 * Landlines are deliberately rejected: every flow that captures a phone here
 * (spinner OTP, ordering, referral) needs a number that can receive SMS.
 */

const BD_MOBILE = /^01[3-9]\d{8}$/

/**
 * Normalizes any reasonable way of writing a BD mobile number.
 *
 * Accepts `01712345678`, `8801712345678`, `+880 1712-345678`, Bengali digits,
 * and the same with spaces, dashes or parentheses. Returns `+8801XXXXXXXXX`,
 * or null when the input is not a valid BD mobile number.
 */
export function normalizeBdPhone(input: string): string | null {
  // Bengali numerals appear whenever someone types on a বাংলা keyboard.
  const western = input.replace(/[০-৯]/g, (d) => String('০১২৩৪৫৬৭৮৯'.indexOf(d)))
  const digits = western.replace(/[^\d+]/g, '')

  let local: string
  if (digits.startsWith('+880')) local = `0${digits.slice(4)}`
  else if (digits.startsWith('880')) local = `0${digits.slice(3)}`
  else if (digits.startsWith('0')) local = digits
  else if (digits.startsWith('1')) local = `0${digits}`
  else return null

  if (!BD_MOBILE.test(local)) return null
  return `+880${local.slice(1)}`
}

/** `+8801712345678` → `01712-345678`, the way numbers are written locally. */
export function formatBdPhone(e164: string): string {
  const local = `0${e164.slice(4)}`
  return `${local.slice(0, 5)}-${local.slice(5)}`
}

/**
 * Masks all but the last three digits, for surfaces that confirm "we sent a
 * code to …678" without repeating the whole number (NFR-6).
 */
export function maskPhone(e164: string): string {
  return `${e164.slice(0, 4)}••••••${e164.slice(-3)}`
}
